import React, { useEffect } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Breadcrumbs, BreadcrumbsItem, PageHeader, PageHeaderContent, PageTitle, Section, SectionHeader, Tooltip } from 'components';
import ConnectedSiteSelector from 'components/SiteSelector/ConnectedSiteSelector';
import { useFeatureFlags } from 'contexts/featureFlags';
import { theme, toRem } from 'design';
import routeConstants, { URL } from 'routes/routeConstants';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { CalculatorRules, getCalculatorRulesRequest, selectCalculatorRules } from 'store/CalculatorRules';
import { getJobTitlesRequest } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { getSiteBalanceRequest, selectSitePOSIntegration } from 'store/Site';
import * as ActionTypes from 'store/Site/types';
import { IStore } from 'store/types';
import { FeatureFlags, PaymentWorkflowTypes, TipCalculatorMethod } from 'types';
import { hasSitePermission } from 'utils';
import { IntegrationRequiredModal, NoCalculatorRulesModal } from './Calculator/components/modals';
import PayoutMethodCard from './components/PayoutMethodCard/PayoutMethodCard';
import { SelectSiteGrid } from './styles';
import { MakePaymentStep, PayoutMethodOption, payoutMethodOptions } from './types';

const ContentWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding-bottom: ${toRem(theme.space['3xl'])};
`;

const SelectWorkflow = () => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId;
  const currentSitePosIntegration = useSelector(selectSitePOSIntegration());
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const isSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.Site.GET_SITE_REQUEST));
  const isCalcRulesLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GET_CALCULATOR_RULES_REQUEST));
  const tipCalculatorFlagEnabled = useFeatureFlags()[FeatureFlags.TIP_CALCULATOR]?.enabled;

  useEffect(() => {
    if (currentSiteId) {
      dispatch(getSiteBalanceRequest(currentSiteId));
      dispatch(getJobTitlesRequest({ siteId: currentSiteId }));
    }
  }, [dispatch, currentSiteId]);

  useEffect(() => {
    if (currentSiteId && tipCalculatorFlagEnabled && currentSite?.settings?.tipCalculator) {
      dispatch(getCalculatorRulesRequest(currentSiteId));
    }
  }, [dispatch, currentSiteId, tipCalculatorFlagEnabled, currentSite?.settings?.tipCalculator]);

  const [showNoCalcRulesModal, hideNoCalcRulesModal] = useModal(() => <NoCalculatorRulesModal hideModal={hideNoCalcRulesModal} closeOnOutSideClick />, []);

  const [showIntegrationRequiredModal, hideIntegrationRequiredModal] = useModal(() => <IntegrationRequiredModal hideModal={hideIntegrationRequiredModal} />, []);

  const onPayoutMethodSelect = (selectedMethod: PaymentWorkflowTypes) => {
    switch (selectedMethod) {
      case PaymentWorkflowTypes.STANDARD:
        browserHistory.push(`${routeConstants.private.makePaymentStandard}/${MakePaymentStep.SelectDate}`);
        break;
      case PaymentWorkflowTypes.UPLOAD:
        browserHistory.push(`${routeConstants.private.makePaymentUpload}/${MakePaymentStep.SelectDate}`);
        break;
      case PaymentWorkflowTypes.CALCULATOR:
        if (!currentCalculatorRules || currentCalculatorRules.method === TipCalculatorMethod.NONE) {
          showNoCalcRulesModal();
        } else if (!currentSitePosIntegration) {
          showIntegrationRequiredModal();
        } else {
          browserHistory.push(routeConstants.private.makePaymentCalculatorDate);
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <PageHeader>
        <Breadcrumbs>
          <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Payments" to={URL.PaymentsOverview} />
          <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Make Payment" />
        </Breadcrumbs>

        <PageHeaderContent>
          <PageTitle title="Select a site & payout method" />
        </PageHeaderContent>
      </PageHeader>

      <Section>
        <ContentWrapper>
          <SectionHeader title="Select Site" dataCy="select-site-header" />
          <ConnectedSiteSelector />
        </ContentWrapper>

        <ContentWrapper>
          <SectionHeader title="Select Payout Method" dataCy="select-payout-header" />
          <SelectSiteGrid>
            {payoutMethodOptions.map((method: PayoutMethodOption) => {
              const { type, icon, desc, permission } = method;

              // Hide calculator option if tip calculator is not enabled for the site
              if (type === PaymentWorkflowTypes.CALCULATOR && !currentSite?.settings?.tipCalculator) return null;

              // Calculator payouts are turned off via feature flag (e.g. during maintenance)
              const calculatorUnavailable = type === PaymentWorkflowTypes.CALCULATOR && !tipCalculatorFlagEnabled;

              return (
                <PayoutMethodCard
                  key={`payout-type-${type}`}
                  title={type}
                  subTitle={desc}
                  icon={icon}
                  isLoading={isSiteLoading || (type === PaymentWorkflowTypes.CALCULATOR && isCalcRulesLoading)}
                  disabled={calculatorUnavailable || !hasSitePermission(currentSite?.permissions, permission)}
                  onClick={() => onPayoutMethodSelect(type)}
                  tooltipId={calculatorUnavailable ? 'calculator-unavailable' : undefined}
                />
              );
            })}
          </SelectSiteGrid>
        </ContentWrapper>
      </Section>
      {!tipCalculatorFlagEnabled && (
        <Tooltip id="calculator-unavailable">
          Tip calculator payments are temporarily unavailable.
          <br />
          Please try again later.
        </Tooltip>
      )}
    </>
  );
};

export default SelectWorkflow;
